import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import ContractPricingDisplay from '../../components/pricing/ContractPricingDisplay';
import axios from '../../utils/axios';
import { useDebounce } from '../../hooks/useDebounce';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
  TagIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import QuickOrderForm from '../../components/b2b/QuickOrderForm';
import CSVUploadModal from '../../components/b2b/CSVUploadModal';
import B2BProductCard from '../../components/b2b/B2BProductCard';
import SmartSearchBar from '../../components/b2b/SmartSearchBar';
import FeaturedProductsPanel from '../../components/b2b/FeaturedProductsPanel';
import ProductQuickView from '../../components/perfume/ProductQuickView';
import FloatingCartButton from '../../components/cart/FloatingCartButton';
import ImageLightbox from '../../components/ui/ImageLightbox';
import { useNavigate } from 'react-router-dom';

const B2BProductsPage = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Bulk operations
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  
  // Quick View Modal
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  
  // Image Lightbox
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 50; // B2B users prefer more items per page

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBrands();
  }, [currentPage, searchTerm, selectedCategory, selectedBrand, sortBy, sortOrder]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        pricing: 'b2b' // Request B2B pricing
      });

      // Only add search and category params if they have values
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }
      if (selectedBrand) {
        params.append('brand_id', selectedBrand);
      }

      const response = await axios.get(`/api/products?${params}`);
      
      setProducts(response.data.products || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error('Error fetching B2B products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get('/api/brands', {
        params: { is_active: true, limit: 100, sort: 'name', order: 'asc' }
      });
      setBrands(response.data.brands || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
      // Silently fail - brands feature might not be available yet
      setBrands([]);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Track products that are being added to cart (to prevent double adding)
  const addingToCartRef = useRef(new Set());

  const handleQuantityChange = (productId, quantity) => {
    const qty = Math.max(0, parseInt(quantity) || 0);
    setQuantities(prev => ({
      ...prev,
      [productId]: qty
    }));
  };

  const handleAddToCart = useCallback(async (product, quantity = 1, resetQuantity = true) => {
    const qty = quantities[product.id] || quantity;
    
    // Check stock availability before adding to cart
    // For agents: check exact quantity, for others: check canOrder flag
    if (product.stock_info) {
      if (!product.stock_info.canOrder) {
        if (!resetQuantity) {
          // Silent fail for auto-add
          return;
        }
        alert('המוצר חסר במלאי');
        return;
      }
      
      // For agents with exact quantity, check if enough stock
      if (product.stock_info.showExactQuantity && product.stock_info.quantity !== undefined) {
        if (product.stock_info.quantity < qty) {
          if (!resetQuantity) {
            // Silent fail for auto-add
            return;
          }
          alert(`אין מספיק מלאי. זמין במלאי: ${product.stock_info.quantity} יחידות`);
          return;
        }
      }
    } else {
      // Fallback for old format
      const availableStock = product.stock_b2b || 0;
      if (availableStock < qty) {
        if (!resetQuantity) {
          // Silent fail for auto-add
          return;
        }
        alert(`אין מספיק מלאי. זמין במלאי עסקי: ${availableStock} יחידות`);
        return;
      }
    }
    
    if (qty > 0) {
      try {
        // Calculate contract pricing for this product and quantity
        const params = new URLSearchParams();
        params.append('quantity', qty.toString());
        if (user.company_id) {
          params.append('company_id', user.company_id);
        }

        const response = await axios.get(`/api/contract-pricing/calculate/${product.id}?${params}`);
        const pricing = response.data.pricing;
        
        // Create product object with calculated B2B price
        const productWithB2BPrice = {
          ...product,
          price: pricing.final_price || pricing.unit_price || product.price_b2b || product.price_b2c,
          original_price: product.price_b2b || product.price_b2c,
          contract_discount: pricing.discount_percentage || 0,
          contract_terms: pricing.contract_terms
        };

        await addToCart(productWithB2BPrice, qty);
        
        // Reset quantity after adding only if resetQuantity is true (manual add)
        if (resetQuantity) {
          setQuantities(prev => ({
            ...prev,
            [product.id]: 0
          }));
        }
      } catch (error) {
        console.error('Error calculating contract pricing:', error);
        // Fallback to B2B price if contract pricing fails
        const productWithB2BPrice = {
          ...product,
          price: product.price_b2b || product.price_b2c
        };
        await addToCart(productWithB2BPrice, qty);
        
        // Reset quantity after adding only if resetQuantity is true (manual add)
        if (resetQuantity) {
          setQuantities(prev => ({
            ...prev,
            [product.id]: 0
          }));
        }
      }
    }
  }, [quantities, addToCart, user]);

  // Auto-add to cart when quantity changes (with debounce)
  const debouncedQuantities = useDebounce(quantities, 500); // Wait 500ms after user stops typing
  const previousQuantitiesRef = useRef({});

  useEffect(() => {
    // Check each product in quantities - only add if quantity changed
    Object.entries(debouncedQuantities).forEach(async ([productId, quantity]) => {
      const qty = parseInt(quantity) || 0;
      const prevQty = parseInt(previousQuantitiesRef.current[productId]) || 0;
      
      // Only process if quantity actually changed
      if (qty === prevQty) {
        return;
      }
      
      // Update previous quantities
      previousQuantitiesRef.current[productId] = qty;
      
      // Only add if quantity > 0 and not already adding
      if (qty > 0 && !addingToCartRef.current.has(productId)) {
        const product = products.find(p => p.id === productId);
        if (product) {
          // Check stock availability
          if (product.stock_info) {
            if (!product.stock_info.canOrder) {
              return; // Don't add if out of stock
            }
            
            // For agents with exact quantity, check if enough stock
            if (product.stock_info.showExactQuantity && product.stock_info.quantity !== undefined) {
              if (product.stock_info.quantity < qty) {
                return; // Don't add if not enough stock
              }
            }
          }
          
          // Mark as adding
          addingToCartRef.current.add(productId);
          
          // Add to cart automatically (without resetting quantity)
          try {
            await handleAddToCart(product, qty, false); // Pass false to not reset quantity
          } catch (error) {
            console.error('Error auto-adding to cart:', error);
          } finally {
            // Remove from adding set after a delay
            setTimeout(() => {
              addingToCartRef.current.delete(productId);
            }, 1000);
          }
        }
      } else if (qty === 0) {
        // If quantity is 0, remove from adding set
        addingToCartRef.current.delete(productId);
      }
    });
  }, [debouncedQuantities, products, handleAddToCart]);

  // Handle negotiation for single product
  const handleNegotiateSingle = (product) => {
    // Store product in localStorage to pass to negotiation page
    const negotiationData = {
      items: [{
        product_id: product.id,
        product: product,
        quantity: quantities[product.id] || 1
      }]
    };
    localStorage.setItem('negotiationData', JSON.stringify(negotiationData));
    navigate('/price-negotiations/new');
  };

  // Handle negotiation for selected products
  const handleNegotiateSelected = () => {
    const items = Array.from(selectedProducts).map(productId => {
      const product = products.find(p => p.id === productId);
      return {
        product_id: product.id,
        product: product,
        quantity: quantities[productId] || 1
      };
    }).filter(item => item.product); // Filter out any null products

    if (items.length === 0) {
      alert('נא לבחור מוצרים ולהזין כמות');
      return;
    }

    const negotiationData = { items };
    localStorage.setItem('negotiationData', JSON.stringify(negotiationData));
    navigate('/price-negotiations/new');
  };

  const handleBulkAddToCart = async () => {
    for (const productId of selectedProducts) {
      const product = products.find(p => p.id === productId);
      const quantity = quantities[productId] || 1;
      
      // Check stock availability before adding to cart
      if (product && quantity > 0) {
        if (product.stock_info) {
          if (!product.stock_info.canOrder) {
            alert(`המוצר ${product.name} חסר במלאי`);
            continue;
          }
          
          // For agents with exact quantity, check if enough stock
          if (product.stock_info.showExactQuantity && product.stock_info.quantity !== undefined) {
            if (product.stock_info.quantity < quantity) {
              alert(`אין מספיק מלאי עבור ${product.name}. זמין במלאי: ${product.stock_info.quantity} יחידות`);
              continue;
            }
          }
        } else {
          // Fallback
          const availableStock = product.stock_b2b || 0;
          if (availableStock < quantity) {
            alert(`אין מספיק מלאי עבור ${product.name}. זמין במלאי עסקי: ${availableStock} יחידות`);
            continue;
          }
        }
        
        try {
          // Calculate contract pricing for this product and quantity
          const params = new URLSearchParams();
          params.append('quantity', quantity.toString());
          if (user.company_id) {
            params.append('company_id', user.company_id);
          }

          const response = await axios.get(`/api/contract-pricing/calculate/${product.id}?${params}`);
          const pricing = response.data.pricing;
          
          // Create product object with calculated B2B price
          const productWithB2BPrice = {
            ...product,
            price: pricing.final_price || pricing.unit_price || product.price_b2b || product.price_b2c,
            original_price: product.price_b2b || product.price_b2c,
            contract_discount: pricing.discount_percentage || 0,
            contract_terms: pricing.contract_terms
          };

          addToCart(productWithB2BPrice, quantity);
        } catch (error) {
          console.error('Error calculating contract pricing for product:', product.id, error);
          // Fallback to B2B price if contract pricing fails
          const productWithB2BPrice = {
            ...product,
            price: product.price_b2b || product.price_b2c
          };
          addToCart(productWithB2BPrice, quantity);
        }
      }
    }
    
    // Clear selections
    setSelectedProducts(new Set());
    setQuantities({});
    setShowBulkActions(false);
  };

  const handleFastCheckout = async () => {
    // Add selected products to cart first
    await handleBulkAddToCart();
    // Navigate to fast checkout
    navigate('/fast-checkout');
  };

  const toggleProductSelection = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const selectAllProducts = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
      setShowBulkActions(true);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(price);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? 
      <ArrowUpIcon className="h-4 w-4" /> : 
      <ArrowDownIcon className="h-4 w-4" />;
  };

  if (!user || (user.role !== 'b2b' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">גישה מוגבלת</h2>
          <p className="text-gray-600">עמוד זה מיועד לקוחות עסקיים בלבד</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50" dir="rtl">
      {/* Enhanced Header with Gradient */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BoltIcon className="h-8 w-8" />
                פורטל הזמנות חכם
              </h1>
              <p className="text-blue-100 mt-2">מערכת AI להזמנות מהירות ויעילות</p>
            </div>
            
            <div className="flex space-x-4 space-x-reverse">
              <button 
                className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-md shadow-sm text-sm font-medium hover:bg-blue-50 focus:ring-2 focus:ring-white transition-all transform hover:scale-105"
                onClick={() => setShowQuickOrder(true)}
              >
                <ClipboardDocumentListIcon className="h-5 w-5 ml-2" />
                הזמנה מהירה
              </button>
              
              <button 
                className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-md shadow-sm text-sm font-medium hover:bg-white/30 transition-all"
                onClick={() => setShowCSVUpload(true)}
              >
                <DocumentTextIcon className="h-5 w-5 ml-2" />
                העלאת CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Smart Search and Quick Actions */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Smart Search - Takes 2 columns */}
            <div className="lg:col-span-2">
              <SmartSearchBar 
                onSearch={(term) => setSearchTerm(term)}
                onProductSelect={(product) => {
                  // Scroll to product in table or add quick action
                  handleQuantityChange(product.id, 1);
                }}
              />
              
              {/* Category & Brand Filters Below Search */}
              <div className="mt-3 flex items-center gap-3">
                <FunnelIcon className="h-5 w-5 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">🏷️ כל הקטגוריות</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">🏢 כל המותגים</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Stats Card */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
              <div className="text-sm font-semibold text-gray-700 mb-2">📊 סטטיסטיקה מהירה</div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">מוצרים זמינים:</span>
                  <span className="font-bold text-blue-600">{products.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">נבחרו:</span>
                  <span className="font-bold text-purple-600">{selectedProducts.size}</span>
                </div>
                {selectedProducts.size > 0 && (
                  <button
                    onClick={handleBulkAddToCart}
                    className="w-full mt-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md text-sm font-medium hover:from-blue-700 hover:to-purple-700 transition-all"
                  >
                    ⚡ הוסף {selectedProducts.size} לעגלה
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products Panel - Admin Promoted Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <FeaturedProductsPanel 
          onProductClick={(product) => {
            // Scroll to product in table
            const element = document.getElementById(`product-${product.id}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('ring-4', 'ring-purple-400');
              setTimeout(() => {
                element.classList.remove('ring-4', 'ring-purple-400');
              }, 2000);
            }
          }}
        />
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-900">
                נבחרו {selectedProducts.size} מוצרים
              </span>
              <div className="flex space-x-2 space-x-reverse">
                {user?.company_id && (
                  <button
                    onClick={() => handleNegotiateSelected()}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4 ml-1" />
                    בקש משא ומתן
                  </button>
                )}
                <button
                  onClick={handleFastCheckout}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
                >
                  <BoltIcon className="h-4 w-4 ml-1" />
                  תשלום מהיר
                </button>
                <button
                  onClick={handleBulkAddToCart}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <ShoppingCartIcon className="h-4 w-4 ml-1" />
                  הוסף הכל לעגלה
                </button>
                <button
                  onClick={() => {
                    setSelectedProducts(new Set());
                    setShowBulkActions(false);
                  }}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  ביטול בחירה
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products - Modern Card Grid View */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mr-4 text-gray-600">טוען מוצרים...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={fetchProducts}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              נסה שוב
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">אין מוצרים זמינים</h3>
            <p className="text-gray-500">נסה לשנות את הקטגוריה או החיפוש</p>
          </div>
        ) : (
          <>
            {/* Modern B2B Products Table */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600">
                <tr>
                  <th className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedProducts.size === products.length && products.length > 0}
                      onChange={selectAllProducts}
                      className="h-4 w-4 text-blue-600 rounded border-white focus:ring-2 focus:ring-white focus:ring-offset-2"
                    />
                  </th>
                  
                  <th 
                    className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => handleSort('sku')}
                  >
                    <div className="flex items-center gap-1">
                      📦 SKU
                      <SortIcon column="sku" />
                    </div>
                  </th>
                  
                  <th 
                    className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      🏷️ שם המוצר
                      <SortIcon column="name" />
                    </div>
                  </th>
                  
                  <th className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                    🏢 מותג
                  </th>
                  
                  <th className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                    📁 קטגוריה
                  </th>
                  
                  <th 
                    className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => handleSort('price_b2b')}
                  >
                    <div className="flex items-center gap-1">
                      💰 מחיר
                      <SortIcon column="price_b2b" />
                    </div>
                  </th>
                  
                  <th 
                    className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => handleSort('stock_b2b')}
                  >
                    <div className="flex items-center gap-1">
                      📊 מלאי עסקי
                      <SortIcon column="stock_b2b" />
                    </div>
                  </th>
                  
                  <th className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                    🔢 כמות
                  </th>
                  
                  <th className="px-4 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                    סה״כ
                  </th>
                </tr>
              </thead>
              
              <tbody className="bg-white divide-y divide-gray-100">
                {products.map((product, index) => (
                  <React.Fragment key={product.id}>
                    <tr 
                      id={`product-${product.id}`}
                      className={`transition-all duration-200 ${
                        selectedProducts.has(product.id) 
                          ? 'bg-blue-50 hover:bg-blue-100' 
                          : index % 2 === 0 
                            ? 'bg-white hover:bg-gray-50' 
                            : 'bg-gray-50/50 hover:bg-gray-100'
                      }`}
                    >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {product.sku}
                      </span>
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="h-10 w-10 flex-shrink-0">
                          <button
                            className="h-10 w-10 rounded-lg overflow-hidden shadow-sm ring-1 ring-gray-200 hover:ring-2 hover:ring-blue-500 transition-all transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => {
                              const imgSrc = product.images?.[0] ? 
                                (product.images[0].startsWith('http') ? product.images[0] : `/api${product.images[0]}`) : 
                                'https://picsum.photos/48/48';
                              setLightboxImage(imgSrc);
                              setLightboxAlt(product.name);
                            }}
                            title="לחץ להגדלה"
                            aria-label={`הגדל תמונה של ${product.name}`}
                          >
                            <img
                              className="h-full w-full object-cover"
                              src={product.images?.[0] ?
                                (product.images[0].startsWith('http') ? product.images[0] : `/api${product.images[0]}`) :
                                'https://picsum.photos/48/48'
                              }
                              alt=""
                            />
                          </button>
                        </div>
                        <div className="max-w-[200px]">
                          <button
                            className="text-xs font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate w-full text-right focus:outline-none focus:underline focus:text-blue-600"
                            onClick={() => setQuickViewProduct(product)}
                            title={product.name}
                            aria-label={`צפייה מהירה ב${product.name}`}
                          >
                            {product.name}
                          </button>
                          {product.short_description && (
                            <div className="text-[10px] text-gray-500 truncate">
                              {product.short_description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Brand Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {product.brandInfo ? (
                        <div className="flex items-center gap-2">
                          {product.brandInfo.logo && (
                            <img
                              src={product.brandInfo.logo}
                              alt={product.brandInfo.name}
                              className="h-6 w-6 object-contain"
                            />
                          )}
                          <span className="text-xs font-medium text-gray-900">
                            {product.brandInfo.name}
                          </span>
                        </div>
                      ) : product.brand ? (
                        <span className="text-xs text-gray-600">{product.brand}</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {product.category?.name || 'ללא קטגוריה'}
                      </span>
                    </td>
                    
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        {/* Check if has contract pricing (mock - will need backend) */}
                        {product.contract_price ? (
                          <>
                            {/* Contract Price - Main */}
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-blue-600">
                                {formatPrice(product.contract_price)}
                              </span>
                              {product.contract_deal && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 ring-1 ring-green-300">
                                  🎁 {product.contract_deal}
                                </span>
                              )}
                            </div>
                            {/* Regular B2B Price - Crossed */}
                            <span className="text-sm text-gray-500 line-through">
                              {formatPrice(product.price_b2b || product.price_b2c)}
                            </span>
                          </>
                        ) : (
                          /* No contract - just show B2B price */
                          <span className="text-base font-bold text-green-600">
                            {formatPrice(product.price_b2b || product.price_b2c)}
                          </span>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(() => {
                        // Use stock_info if available
                        if (product.stock_info) {
                          const status = product.stock_info.status;
                          const statusConfig = {
                            'out_of_stock': { icon: '❌', text: status.text || 'חסר במלאי', color: 'bg-red-100 text-red-700 ring-1 ring-red-300' },
                            'low_stock': { icon: '⚠️', text: status.text || 'יחידות אחרונות', color: 'bg-yellow-100 text-yellow-700 ring-1 ring-yellow-300' },
                            'in_stock': { icon: '✅', text: status.text || 'זמין במלאי', color: 'bg-green-100 text-green-700 ring-1 ring-green-300' }
                          };
                          
                          // For agents, show exact quantity
                          if (product.stock_info.showExactQuantity && product.stock_info.quantity !== undefined) {
                            const qty = product.stock_info.quantity;
                            const config = qty > 50 ? statusConfig['in_stock'] : qty > 0 ? statusConfig['low_stock'] : statusConfig['out_of_stock'];
                            return (
                              <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg ${config.color}`}>
                                {config.icon} {qty} יח'
                              </span>
                            );
                          }
                          
                          // For regular customers, show status only
                          const config = statusConfig[status.status] || statusConfig['in_stock'];
                          return (
                            <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg ${config.color}`}>
                              {config.icon} {config.text}
                            </span>
                          );
                        }
                        
                        // Fallback
                        const stockQty = product.stock_b2b || 0;
                        const config = stockQty > 50 
                          ? { icon: '✅', text: 'זמין במלאי', color: 'bg-green-100 text-green-700 ring-1 ring-green-300' }
                          : stockQty > 0 
                            ? { icon: '⚠️', text: 'יחידות אחרונות', color: 'bg-yellow-100 text-yellow-700 ring-1 ring-yellow-300' }
                            : { icon: '❌', text: 'חסר במלאי', color: 'bg-red-100 text-red-700 ring-1 ring-red-300' };
                        return (
                          <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg ${config.color}`}>
                            {config.icon} {config.text}
                          </span>
                        );
                      })()}
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max={product.stock_info?.showExactQuantity ? (product.stock_info.quantity || 999) : 999}
                          value={quantities[product.id] || ''}
                          onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                          placeholder="0"
                          className="w-16 px-2 py-1.5 text-xs font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                        />
                        {/* Quick Quantity Buttons */}
                        <div className="flex gap-0.5">
                          <button
                            onClick={() => handleQuantityChange(product.id, 1)}
                            className="px-2 py-1 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-all"
                            title="הוסף 1"
                          >
                            +1
                          </button>
                          <button
                            onClick={() => handleQuantityChange(product.id, 5)}
                            className="px-2 py-1 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-all"
                            title="הוסף 5"
                          >
                            +5
                          </button>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(() => {
                        const quantity = parseInt(quantities[product.id]) || 0;
                        const unitPrice = product.contract_price || product.price_b2b || product.price_b2c || 0;
                        const total = quantity * unitPrice;
                        
                        if (quantity === 0) {
                          return (
                            <span className="text-xs text-gray-400">₪0.00</span>
                          );
                        }
                        
                        return (
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-green-600">
                              {formatPrice(total)}
                            </span>
                            {quantity > 1 && (
                              <span className="text-[10px] text-gray-500">
                                {formatPrice(unitPrice)} × {quantity}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    הקודם
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    הבא
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      מציג{' '}
                      <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                      {' '}עד{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, products.length + (currentPage - 1) * itemsPerPage)}
                      </span>
                      {' '}מתוך{' '}
                      <span className="font-medium">{totalPages * itemsPerPage}</span>
                      {' '}תוצאות
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px space-x-reverse" aria-label="Pagination">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } ${page === 1 ? 'rounded-r-md' : page === totalPages ? 'rounded-l-md' : ''}`}
                        >
                          {page}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {/* Quick Order Modal */}
      {showQuickOrder && (
        <QuickOrderForm onClose={() => setShowQuickOrder(false)} />
      )}

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <CSVUploadModal 
          isOpen={showCSVUpload} 
          onClose={() => setShowCSVUpload(false)} 
        />
      )}

      {/* Product Quick View Modal */}
      <ProductQuickView
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAddToCart={(product, qty) => {
          handleQuantityChange(product.id, qty);
          // Auto-add will happen via useEffect with debounce
        }}
        onAddToWishlist={(product) => {
          // Optional: Add wishlist functionality if needed
          console.log('Add to wishlist:', product);
        }}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        src={lightboxImage}
        alt={lightboxAlt}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />

      {/* Floating Cart Button */}
      <FloatingCartButton />
    </div>
  );
};

export default B2BProductsPage;
